/**
 * Username rules (brief §11).
 *
 * Pure, like everything in this directory: no clock, no randomness, no I/O, and no `node:`
 * imports — the registration form in the browser validates with exactly this code, so the
 * message you get while typing is the message the server would give you.
 */

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 20;

/** Lowercase, starts alphanumeric, then letters, digits, underscore or hyphen. */
const SHAPE = /^[a-z0-9][a-z0-9_-]{2,19}$/;

/**
 * Names nobody may take.
 *
 * `admin` and friends are the obvious half. The second half is subtler: `me`, `null`,
 * `undefined` and `deleted` are the strings that appear where a username is expected when
 * something has gone wrong, and a real account holding one of them turns a display bug into a
 * convincing impersonation.
 */
export const RESERVED = new Set([
  'admin',
  'administrator',
  'root',
  'system',
  'hotspotter',
  'support',
  'help',
  'api',
  'www',
  'app',
  'me',
  'null',
  'undefined',
  'none',
  'anonymous',
  'deleted',
  // The tombstone account anonymised reports are moved to (brief §18). Reserved so nobody can
  // register it and inherit somebody else's history.
  'geloescht',
  'unknown',
  'moderator',
  'mod',
]);

/**
 * The form two names are compared under.
 *
 * Underscores and hyphens are removed, so `max_mueller`, `max-mueller` and `maxmueller` are
 * one name. In a group of friends those are one person's three attempts at the same handle,
 * and letting all three exist is a way to be mistaken for somebody else on purpose.
 */
export function canonicalUsername(username: string): string {
  return username.toLowerCase().replaceAll(/[_-]/g, '');
}

export type UsernameProblem =
  | 'too_short'
  | 'too_long'
  | 'bad_characters'
  | 'reserved'
  | 'canonical_empty';

export type UsernameCheck =
  | { ok: true; username: string; canonical: string }
  | { ok: false; problem: UsernameProblem };

export function checkUsername(input: string): UsernameCheck {
  const username = input.trim().toLowerCase();

  if (username.length < USERNAME_MIN) return { ok: false, problem: 'too_short' };
  if (username.length > USERNAME_MAX) return { ok: false, problem: 'too_long' };
  if (!SHAPE.test(username)) return { ok: false, problem: 'bad_characters' };

  const canonical = canonicalUsername(username);
  // `___` passes the shape test — it starts with no separator only because of the first
  // character rule, and `a__` canonicalises to `a`. But a name made entirely of separators
  // after the first character can canonicalise to something far shorter than it looks.
  if (canonical.length < USERNAME_MIN) return { ok: false, problem: 'canonical_empty' };
  if (RESERVED.has(canonical)) return { ok: false, problem: 'reserved' };

  return { ok: true, username, canonical };
}

/** For the browser: one sentence per problem, in the app's language. */
export function describeUsernameProblem(problem: UsernameProblem): string {
  switch (problem) {
    case 'too_short':
      return `Mindestens ${USERNAME_MIN} Zeichen.`;
    case 'too_long':
      return `Höchstens ${USERNAME_MAX} Zeichen.`;
    case 'bad_characters':
      return 'Nur Kleinbuchstaben, Ziffern, _ und -, und das erste Zeichen ist ein Buchstabe oder eine Ziffer.';
    case 'reserved':
      return 'Dieser Name ist reserviert.';
    case 'canonical_empty':
      return 'Zu viele Trennzeichen — es müssen mindestens drei Buchstaben oder Ziffern übrig bleiben.';
  }
}
